/**
 * GlobalView — Loading Screen
 *
 * Spy cockpit HUD overlay displayed during app initialisation.
 * Stays visible until globe tiles are loaded, then reveals the app
 * with a scan effect: blur resolves from pixelated to sharp.
 */

import styles from './LoadingScreen.module.css';

interface LoadingScreenProps {
  /** When true, triggers the scan reveal animation */
  fadingOut?: boolean;
  /** Called after the reveal animation completes */
  onFadeComplete?: () => void;
}

export default function LoadingScreen({ fadingOut, onFadeComplete }: LoadingScreenProps) {
  return (
    <div
      className={`${styles.overlay} ${fadingOut ? styles.fadeOut : ''}`}
      id="loading-screen"
      onAnimationEnd={(e) => {
        // Only fire callback when the overlay animation ends (not child animations)
        if (fadingOut && e.target === e.currentTarget) {
          onFadeComplete?.();
        }
      }}
    >
      {/* Layer 1: Solid dark background */}
      <div className={styles.bgLayer} />

      {/* Layer 2: Blur overlay — creates the scan resolve effect */}
      <div className={styles.blurLayer} />

      {/* Layer 3: All HUD content */}
      <div className={styles.hudContent}>
        {/* Grid background */}
        <div className={styles.grid} />

        {/* Scan line */}
        <div className={styles.scanLine} />

        {/* Radial glow */}
        <div className={styles.radialGlow} />

        {/* HUD frame corners */}
        <div className={styles.hudFrame} />

        {/* Central content */}
        <div className={styles.center}>
          {/* Targeting reticle */}
          <div className={styles.reticle}>
            <div className={styles.reticleRing} />
            <div className={styles.reticleRingInner} />
            <div className={styles.reticleCrosshair} />
            <div className={styles.reticleCorner} />
            <div className={styles.reticleCorner} />
            <div className={styles.reticleCorner} />
            <div className={styles.reticleCorner} />
            <div className={styles.centerDot} />
          </div>

          {/* Title */}
          <h1 className={styles.title}>GLOBALVIEW</h1>
          <p className={styles.subtitle}>System Initializing</p>

          {/* Progress bar */}
          <div className={styles.progressTrack}>
            <div className={styles.progressBar} />
          </div>

          {/* Status */}
          <div className={styles.statusRow}>
            <div className={styles.statusDot} />
            <span className={styles.statusText}>CONNECTING</span>
          </div>
        </div>

        {/* HUD corner readouts */}
        <div className={`${styles.hudCorner} ${styles.hudTopLeft}`}>
          <div><span className={styles.hudLabel}>SYS </span><span className={styles.hudValue}>ONLINE</span></div>
          <div><span className={styles.hudLabel}>LAT </span><span className={styles.hudValue}>48.1351°N</span></div>
          <div><span className={styles.hudLabel}>LON </span><span className={styles.hudValue}>11.5820°E</span></div>
        </div>

        <div className={`${styles.hudCorner} ${styles.hudTopRight}`}>
          <div><span className={styles.hudLabel}>NET </span><span className={styles.hudValue}>SECURE</span></div>
          <div><span className={styles.hudLabel}>ENC </span><span className={styles.hudValue}>AES-256</span></div>
          <div><span className={styles.hudLabel}>SIG </span><span className={styles.hudValue}>■■■■□</span></div>
        </div>

        <div className={`${styles.hudCorner} ${styles.hudBottomLeft}`}>
          <div><span className={styles.hudLabel}>MOD </span><span className={styles.hudValue}>STANDBY</span></div>
          <div><span className={styles.hudLabel}>FRQ </span><span className={styles.hudValue}>2.4 GHz</span></div>
        </div>

        <div className={`${styles.hudCorner} ${styles.hudBottomRight}`}>
          <div><span className={styles.hudLabel}>VER </span><span className={styles.hudValue}>v0.1.0</span></div>
          <div><span className={styles.hudLabel}>BLD </span><span className={styles.hudValue}>PROD</span></div>
        </div>
      </div>
    </div>
  );
}

